import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography, Tab, Tabs, Chip } from '@material-ui/core';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RouterIcon from '@mui/icons-material/Router';
import PropaneTankIcon from '@mui/icons-material/PropaneTank';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { GlobalBox } from 'components/Box/GlobalBox';
import { LoadingComponent } from 'components/Loading/LoadingComponent';
import RegularButton from 'components/CustomButtons/Button';
import CardTable from 'components/CardTable/CardTable';
import {
  getIotDeviceById,
  getAssignHistory,
} from 'redux/actions/iotDevices/iotDevicesActions';

const STATUS_COLOR = { ACTIVE: '#2e7d32', INACTIVE: '#c62828', MAINTENANCE: '#e65100', RETIRED: '#616161' };

const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" py={0.5} borderBottom="1px solid #f0f0f0">
    <Typography variant="body2" color="textSecondary">{label}</Typography>
    <Typography variant="body2"><strong>{value ?? '—'}</strong></Typography>
  </Box>
);

const historyColumns = [
  { Header: 'Tanque', id: 'tankSerie', accessor: d => d.tank?.serie ?? '—', filterable: false },
  { Header: 'Evento', accessor: 'event', filterable: false, Cell: ({ value }) => ({ ASSIGNED: 'Vinculado', UNASSIGNED: 'Desvinculado' }[value] ?? value) },
  { Header: 'Motivo', accessor: 'changeReason', filterable: false, Cell: ({ value }) => value ?? '—' },
  { Header: 'Fecha', accessor: 'created', filterable: false },
  { Header: 'Usuario', accessor: 'createdBy', filterable: false },
];

export const IotDeviceDetail = () => {
  const { id } = useParams();
  const hist = useHistory();
  const dispatch = useDispatch();
  const { iotDeviceSelected, assignHistory } = useSelector(({ iotDevices }) => iotDevices);
  const { stationsList } = useSelector(({ companies }) => companies);
  const { isLoadingComp } = useSelector(({ search }) => search);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    dispatch(getIotDeviceById(id));
  }, [id]);

  useEffect(() => {
    if (String(iotDeviceSelected?.id) === id) {
      dispatch(getAssignHistory(id));
    }
  }, [iotDeviceSelected?.id]);

  const device = iotDeviceSelected;
  const commodato = device?.tank?.commodato;
  const address = commodato?.account?.address;
  const isLoading = isLoadingComp?.loadingIotDevices;

  const addressText = address
    ? [address.street, address.extNumber, address.colony, address.municipality, address.state, address.postalCode]
        .filter(Boolean).join(', ')
    : null;

  return (
    <GlobalBox
      title={
        <Box>
          <Typography variant="h6">{device?.iotIdentity ?? 'Dispositivo IoT'}</Typography>
          {device?.status && (
            <Chip
              label={device.status}
              size="small"
              style={{ backgroundColor: STATUS_COLOR[device.status] ?? '#555', color: '#fff', marginTop: 4 }}
            />
          )}
        </Box>
      }
      icon={<RouterIcon />}
      controls={
        <RegularButton justIcon round color="twitter" size="sm" tooltip="Regresar" onClick={() => hist.push('/admin/iot-devices')}>
          <ArrowBackIcon style={{ margin: 0 }} />
        </RegularButton>
      }
    >
      {isLoading ? (
        <LoadingComponent />
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary">
            <Tab label="Información" />
            <Tab label="Historial de vinculaciones" />
          </Tabs>

          {/* TAB 0 — Información */}
          {tab === 0 && (
            <Grid container spacing={2} style={{ marginTop: 16 }}>
              {/* Dispositivo */}
              <Grid item xs={12} md={4}>
                <Box p={2} border="1px solid #e0e0e0" borderRadius={8}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <RouterIcon style={{ color: '#004F9F' }} />
                    <Typography variant="subtitle1"><strong>Dispositivo</strong></Typography>
                  </Box>
                  <InfoRow label="Identidad" value={device?.iotIdentity} />
                  <InfoRow label="Proveedor" value={device?.provider} />
                  <InfoRow label="Marca" value={device?.brand} />
                  <InfoRow label="Modelo" value={device?.model} />
                  <InfoRow label="Tipo medición" value={device?.measurementType} />
                  <InfoRow label="Planta" value={stationsList.find(s => s.id === device?.idStation)?.label ?? device?.station?.name} />
                  <InfoRow label="Instalación" value={device?.installationDate} />
                  <InfoRow label="Última comunicación" value={device?.lastCommunication} />
                  <InfoRow label="Observaciones" value={device?.observations} />
                </Box>
              </Grid>

              {/* Tanque */}
              <Grid item xs={12} md={4}>
                <Box p={2} border="1px solid #e0e0e0" borderRadius={8}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <PropaneTankIcon style={{ color: '#004F9F' }} />
                    <Typography variant="subtitle1"><strong>Tanque comodato</strong></Typography>
                  </Box>
                  {device?.tank ? (
                    <>
                      <InfoRow label="Serie" value={device.tank.serie} />
                      <InfoRow label="Activo Fijo" value={device.tank.fixedAssetNum} />
                      <InfoRow label="Capacidad" value={device.tank.capacity ? `${device.tank.capacity} L` : null} />
                      <InfoRow label="Planta" value={commodato?.station?.name} />
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary">Sin tanque vinculado</Typography>
                  )}
                </Box>

                {/* Última medición */}
                {device?.lastMeasurement && (
                  <Box p={2} border="1px solid #e0e0e0" borderRadius={8} mt={2}>
                    <Typography variant="subtitle1"><strong>Última medición</strong></Typography>
                    <InfoRow label="Fecha" value={device.lastMeasurement.readingDate} />
                    <InfoRow label="Nivel" value={`${device.lastMeasurement.levelPct}%`} />
                    <InfoRow label="Litros" value={device.lastMeasurement.liters} />
                    <InfoRow label="Batería" value={`${device.lastMeasurement.batteryLevel}%`} />
                    <InfoRow label="Señal" value={`${device.lastMeasurement.signalStrength}%`} />
                    <InfoRow label="Estatus" value={device.lastMeasurement.readStatus} />
                  </Box>
                )}
              </Grid>

              {/* Dirección, Cuenta y Cliente */}
              <Grid item xs={12} md={4}>
                <Box p={2} border="1px solid #e0e0e0" borderRadius={8}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AccountCircleIcon style={{ color: '#004F9F' }} />
                    <Typography variant="subtitle1"><strong>Cliente / Cuenta</strong></Typography>
                  </Box>
                  {commodato ? (
                    <>
                      <InfoRow label="Código Cliente" value={commodato.customer?.number} />
                      <InfoRow label="Nombre Cliente" value={commodato.customer?.name} />
                      <InfoRow label="Código Cuenta" value={commodato.account?.code} />
                      <InfoRow label="Planta" value={commodato?.station?.name} />
                      {addressText && <InfoRow label="Dirección" value={addressText} />}
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary">Sin cuenta asociada</Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}

          {/* TAB 1 — Historial de vinculaciones */}
          {tab === 1 && (
            <Box mt={2}>
              <CardTable
                data={assignHistory}
                columns={historyColumns}
                blockBtn
                tableKey="iotAssignHistory"
              />
            </Box>
          )}
        </>
      )}
    </GlobalBox>
  );
};
